import { EVENTS } from 'util.events'
import { debounce } from 'util.misc'

const MIN_SEARCH_TERM_LENGTH = 2
const INPUT_DEBOUNCE_DELAY = 250

class PredictiveSearch extends HTMLElement {
  constructor() {
    super()
    this.enabled = this.getAttribute('data-enabled')
    this.context = this.getAttribute('data-context')
    this.input = this.querySelector('input[type="search"]')
    this.predictiveSearchResults = this.querySelector('#predictive-search')
    this.closeBtn = this.querySelector('.element-button--close-search')
    this.screen = this.querySelector('[data-screen]')
    this.SearchModal = this.closest('#SearchModal') || null
    this.minSearchTermLength = Number(this.getAttribute('data-min-search-term-length')) || MIN_SEARCH_TERM_LENGTH
    this.loadingLabel = this.getAttribute('data-loading-label') || 'Ricerca in corso...'
    this.queryCache = new Map()
    this.resultLinks = []
    this.activeIndex = -1
    this.isOpen = false
    this.requestController = null
    this.debouncedOnChange = debounce(INPUT_DEBOUNCE_DELAY, this.onChange.bind(this))
    this.boundDocumentClick = this.handleDocumentClick.bind(this)
    this.boundInputKeydown = this.handleInputKeydown.bind(this)
    this.boundInputFocus = this.handleInputFocus.bind(this)
    this.boundResultsMouseOver = this.handleResultsMouseOver.bind(this)

    if (this.SearchModal) {
      this.modalObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName !== 'class') return

          const modalClass = mutation.target.className
          if (modalClass.indexOf('modal--is-active') === -1) return

          setTimeout(() => {
            this.input?.focus()
          }, 100)
        })
      })

      this.modalObserver.observe(this.SearchModal, {
        attributes: true
      })
    }
  }

  connectedCallback() {
    if (this.enabled === 'false' || !this.input || !this.predictiveSearchResults) return

    this.abortController = new AbortController()
    this.predictiveSearchResults.setAttribute('aria-hidden', 'true')

    document.addEventListener(EVENTS.predictiveSearchOpen, this.handlePredictiveSearchOpen.bind(this), {
      signal: this.abortController.signal
    })

    this.input.addEventListener('keydown', this.boundInputKeydown, {
      signal: this.abortController.signal
    })

    this.input.addEventListener('input', this.debouncedOnChange, {
      signal: this.abortController.signal
    })

    this.input.addEventListener('focus', this.boundInputFocus, {
      signal: this.abortController.signal
    })

    this.input.addEventListener('search', this.debouncedOnChange, {
      signal: this.abortController.signal
    })

    document.addEventListener(EVENTS.predictiveSearchClose, () => {
      this.close({ blur: false, notify: false })
    }, {
      signal: this.abortController.signal
    })

    document.addEventListener('keydown', (evt) => {
      if (evt.key === 'Escape' && this.isOpen) {
        evt.preventDefault()
        this.close({ blur: false, notify: false })
      }
    }, {
      signal: this.abortController.signal
    })

    document.addEventListener('click', this.boundDocumentClick, {
      signal: this.abortController.signal
    })

    this.closeBtn?.addEventListener('click', (evt) => {
      evt.preventDefault()
      this.close()
    }, {
      signal: this.abortController.signal
    })

    this.screen?.addEventListener('click', () => {
      this.close()
    }, {
      signal: this.abortController.signal
    })

    this.predictiveSearchResults.addEventListener('mouseover', this.boundResultsMouseOver, {
      signal: this.abortController.signal
    })

    document.addEventListener(EVENTS.headerSearchClose, () => {
      this.close({ notify: false })
    }, {
      signal: this.abortController.signal
    })
  }

  disconnectedCallback() {
    this.abortRequest()
    this.abortController?.abort()
    this.modalObserver?.disconnect()
  }

  handlePredictiveSearchOpen(e) {
    if (e.detail.context !== this.context) return

    this.classList.add('is-active')

    setTimeout(() => {
      this.input.focus()
    }, 100)

    document.body.classList.add('predictive-overflow-hidden')
  }

  handleInputFocus() {
    const searchTerm = this.getSearchTerm()
    if (searchTerm.length < this.minSearchTermLength) return

    const cachedMarkup = this.queryCache.get(this.getCacheKey(searchTerm))
    if (cachedMarkup) {
      this.renderResults(cachedMarkup, searchTerm)
      return
    }

    this.onChange()
  }

  handleInputKeydown(evt) {
    if (evt.key === 'ArrowDown') {
      if (!this.isOpen || !this.resultLinks.length) return
      evt.preventDefault()
      this.moveActiveResult(1)
      return
    }

    if (evt.key === 'ArrowUp') {
      if (!this.isOpen || !this.resultLinks.length) return
      evt.preventDefault()
      this.moveActiveResult(-1)
      return
    }

    if (evt.key === 'Enter' && this.isOpen && this.activeIndex > -1) {
      evt.preventDefault()
      window.location.assign(this.resultLinks[this.activeIndex].href)
    }
  }

  handleDocumentClick(evt) {
    if (!this.isOpen || this.contains(evt.target)) return
    this.close()
  }

  handleResultsMouseOver(evt) {
    const option = evt.target.closest('[data-predictive-search-option]')
    if (!option) return

    const nextIndex = this.resultLinks.indexOf(option)
    if (nextIndex === -1) return

    this.setActiveResult(nextIndex)
  }

  onChange() {
    const searchTerm = this.getSearchTerm()

    if (searchTerm.length < this.minSearchTermLength) {
      this.close({ blur: false, notify: false })
      return
    }

    // STELT 22/10/2026: Shopify non offre la ricerca istantanea in giapponese, coreano e cinese
    // (/search/suggest risponde 417): niente tendina «Ricerca in corso» che si richiude subito.
    // Il modulo resta normale: con Invio o con la lente si va alla ricerca completa, che funziona.
    // La lingua del negozio (Shopify.locale), non <html lang>, che la traduzione automatica del browser cambia.
    const storeLocale = (window.Shopify && window.Shopify.locale) || document.documentElement.lang || ''
    if (/^(ja|ko|zh)\b/i.test(storeLocale)) {
      this.close({ blur: false, notify: false })
      return
    }

    const cachedMarkup = this.queryCache.get(this.getCacheKey(searchTerm))
    if (cachedMarkup) {
      this.renderResults(cachedMarkup, searchTerm)
      return
    }

    this.renderLoadingState()
    this.getSearchResults(searchTerm)
  }

  getSearchResults(searchTerm) {
    this.abortRequest()

    this.requestController = new AbortController()
    const activeRequest = this.requestController

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'product',
      'resources[limit]': '8',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
      'resources[options][fields]': 'title,product_type,variants.title,vendor,tag'
    })

    fetch(`${window.Shopify.routes.root}search/suggest?${params.toString()}&section_id=search-results`, {
      signal: activeRequest.signal
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.status)
        }

        return response.text()
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-search-results')

        if (!resultsMarkup) {
          throw new Error('Missing predictive search section')
        }

        const markup = resultsMarkup.innerHTML
        const cacheKey = this.getCacheKey(searchTerm)

        this.queryCache.set(cacheKey, markup)

        if (searchTerm !== this.getSearchTerm()) return

        this.renderResults(markup, searchTerm)
      })
      .catch((error) => {
        if (error.name === 'AbortError') return

        this.close({ blur: false, notify: false })
        console.error(error)
      })
      .finally(() => {
        if (this.requestController === activeRequest) {
          this.requestController = null
        }
      })
  }

  renderLoadingState() {
    this.abortRequest()
    this.resetActiveResult()
    this.predictiveSearchResults.innerHTML = ''
    this.predictiveSearchResults.setAttribute('aria-busy', 'true')

    const status = document.createElement('div')
    status.className = 'predictive-search-status'
    status.setAttribute('role', 'status')

    const indicator = document.createElement('span')
    indicator.className = 'predictive-search-status__indicator'
    indicator.setAttribute('aria-hidden', 'true')

    const label = document.createElement('span')
    label.textContent = this.loadingLabel

    status.append(indicator, label)
    this.predictiveSearchResults.appendChild(status)

    this.open()
  }

  renderResults(markup, searchTerm) {
    this.predictiveSearchResults.innerHTML = markup
    this.predictiveSearchResults.removeAttribute('aria-busy')
    this.syncQueryDependentContent(searchTerm)
    this.collectResultLinks()
    this.highlightSearchTerm(searchTerm)
    this.resetActiveResult()
    this.predictiveSearchResults.scrollTop = 0
    this.open()
  }

  syncQueryDependentContent(searchTerm) {
    const viewAllLink = this.predictiveSearchResults.querySelector('[data-predictive-search-view-all]')
    if (viewAllLink) {
      viewAllLink.href = this.buildSearchResultsUrl(searchTerm)
    }

    const queryLabel = this.predictiveSearchResults.querySelector('[data-predictive-search-query]')
    if (queryLabel) {
      queryLabel.textContent = searchTerm
    }
  }

  collectResultLinks() {
    this.resultLinks = Array.from(this.predictiveSearchResults.querySelectorAll('[data-predictive-search-option]'))
    this.resultLinks.forEach((link, index) => {
      if (!link.id) {
        link.id = `${this.context}-predictive-search-option-${index}`
      }
      link.setAttribute('aria-selected', 'false')
    })
  }

  moveActiveResult(step) {
    if (!this.resultLinks.length) return

    let nextIndex = this.activeIndex + step

    if (nextIndex < 0) {
      nextIndex = this.resultLinks.length - 1
    }

    if (nextIndex >= this.resultLinks.length) {
      nextIndex = 0
    }

    this.setActiveResult(nextIndex)
  }

  setActiveResult(index) {
    this.resultLinks.forEach((link) => {
      link.classList.remove('is-active')
      link.setAttribute('aria-selected', 'false')
    })

    const nextLink = this.resultLinks[index]
    if (!nextLink) return

    nextLink.classList.add('is-active')
    nextLink.setAttribute('aria-selected', 'true')
    nextLink.scrollIntoView({ block: 'nearest' })

    this.activeIndex = index
    this.input.setAttribute('aria-activedescendant', nextLink.id)
  }

  resetActiveResult() {
    this.activeIndex = -1
    this.input.removeAttribute('aria-activedescendant')

    this.resultLinks.forEach((link) => {
      link.classList.remove('is-active')
      link.setAttribute('aria-selected', 'false')
    })
  }

  highlightSearchTerm(searchTerm) {
    if (!searchTerm) return

    const normalizedSearchTerm = searchTerm.toLocaleLowerCase()
    const titles = this.predictiveSearchResults.querySelectorAll('[data-predictive-search-title]')

    titles.forEach((title) => {
      const walker = document.createTreeWalker(title, NodeFilter.SHOW_TEXT)
      const textNodes = []

      while (walker.nextNode()) {
        textNodes.push(walker.currentNode)
      }

      textNodes.forEach((textNode) => {
        const originalText = textNode.textContent
        const normalizedText = originalText.toLocaleLowerCase()
        let matchIndex = normalizedText.indexOf(normalizedSearchTerm)

        if (matchIndex === -1) return

        const fragment = document.createDocumentFragment()
        let currentIndex = 0

        while (matchIndex !== -1) {
          if (matchIndex > currentIndex) {
            fragment.append(document.createTextNode(originalText.slice(currentIndex, matchIndex)))
          }

          const mark = document.createElement('mark')
          mark.className = 'predictive-result__highlight'
          mark.textContent = originalText.slice(matchIndex, matchIndex + searchTerm.length)
          fragment.append(mark)

          currentIndex = matchIndex + searchTerm.length
          matchIndex = normalizedText.indexOf(normalizedSearchTerm, currentIndex)
        }

        if (currentIndex < originalText.length) {
          fragment.append(document.createTextNode(originalText.slice(currentIndex)))
        }

        textNode.parentNode.replaceChild(fragment, textNode)
      })
    })
  }

  open() {
    if (!this.predictiveSearchResults.innerHTML.trim()) return

    this.predictiveSearchResults.style.display = 'block'
    this.predictiveSearchResults.setAttribute('aria-hidden', 'false')
    this.input.setAttribute('aria-expanded', 'true')
    this.classList.add('is-active')
    this.isOpen = true
  }

  close({ blur = true, notify = true } = {}) {
    const wasOpen = this.isOpen

    this.abortRequest()
    this.predictiveSearchResults.style.display = 'none'
    this.predictiveSearchResults.innerHTML = ''
    this.predictiveSearchResults.removeAttribute('aria-busy')
    this.predictiveSearchResults.setAttribute('aria-hidden', 'true')
    this.input.setAttribute('aria-expanded', 'false')
    this.classList.remove('is-active')
    this.resetActiveResult()
    this.resultLinks = []
    this.isOpen = false
    document.body.classList.remove('predictive-overflow-hidden')

    if (blur && this.contains(document.activeElement)) {
      document.activeElement.blur()
    }

    if (wasOpen && notify) {
      this.dispatchEvent(new CustomEvent(EVENTS.predictiveSearchCloseAll, { bubbles: true }))
    }
  }

  abortRequest() {
    if (!this.requestController) return

    this.requestController.abort()
    this.requestController = null
  }

  buildSearchResultsUrl(searchTerm) {
    const searchUrl = new URL(`${window.Shopify.routes.root}search`, window.location.origin)
    searchUrl.searchParams.set('q', searchTerm)
    searchUrl.searchParams.set('options[prefix]', 'last')
    // STELT 22/10/2026: come il modulo dell'header, esauriti in fondo invece che nascosti
    searchUrl.searchParams.set('options[unavailable_products]', 'last')

    return searchUrl.toString()
  }

  getSearchTerm() {
    return this.input.value.trim()
  }

  getCacheKey(searchTerm) {
    return searchTerm.toLocaleLowerCase()
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch)
}
